import { forwardRef } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

function getWeekDates(weekNum, year) {
  const jan1 = new Date(year, 0, 1);
  const dayOffset = (jan1.getDay() + 6) % 7;
  const firstMonday = new Date(year, 0, 1 + (7 - dayOffset) % 7);
  if (jan1.getDay() === 1) firstMonday.setDate(jan1.getDate());
  const weekStart = new Date(firstMonday);
  weekStart.setDate(firstMonday.getDate() + (weekNum - 1) * 7);
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    dates.push(d);
  }
  return dates;
}

function formatSlot(debut, fin) {
  if (!debut || !fin) return null;
  return `${debut} - ${fin}`;
}

export const PrintableWeeklyPlanning = forwardRef(function PrintableWeeklyPlanning(
  { data, semaine, year, employes, syntheseData },
  ref
) {
  if (!semaine) return null;

  const weekDates = getWeekDates(semaine, year || new Date().getFullYear());
  const uniqueEmployes = employes?.length
    ? employes
    : [...new Set(data.map(d => d.employe))].sort();

  // Build grid: employee -> date -> slots
  const grid = {};
  const employeTotals = {};
  const dayTotals = {};

  uniqueEmployes.forEach(emp => {
    grid[emp] = {};
    employeTotals[emp] = 0;
  });

  data.forEach(item => {
    if (!uniqueEmployes.includes(item.employe)) return;
    const dateKey = item.date;
    if (!grid[item.employe][dateKey]) {
      grid[item.employe][dateKey] = { midi: null, soir: null, total: 0 };
    }
    grid[item.employe][dateKey].midi = formatSlot(item.debut_midi, item.fin_midi);
    grid[item.employe][dateKey].soir = formatSlot(item.debut_soir, item.fin_soir);
    grid[item.employe][dateKey].total = item.total || 0;
    employeTotals[item.employe] = (employeTotals[item.employe] || 0) + (item.total || 0);
  });

  weekDates.forEach(d => {
    const key = format(d, 'yyyy-MM-dd');
    let total = 0;
    uniqueEmployes.forEach(emp => {
      total += grid[emp]?.[key]?.total || 0;
    });
    dayTotals[key] = total;
  });

  const grandTotal = Object.values(employeTotals).reduce((s, v) => s + v, 0);
  const dateRange = weekDates.length >= 2
    ? `${format(weekDates[0], 'dd/MM', { locale: fr })} au ${format(weekDates[6], 'dd/MM/yyyy', { locale: fr })}`
    : '';

  return (
    <div ref={ref} className="printable-planning hidden print:block">
      <style>{`
        @media print {
          @page { size: landscape; margin: 10mm; }
          .printable-planning { display: block !important; }
          .printable-planning * { visibility: visible; }
        }
        .printable-planning { font-family: Arial, sans-serif; color: #111; }
        .printable-planning table { width: 100%; border-collapse: collapse; font-size: 11px; }
        .printable-planning th, .printable-planning td { border: 1px solid #999; padding: 4px 6px; text-align: center; vertical-align: middle; }
        .printable-planning th { background: #1a1a2e; color: white; font-weight: 600; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
        .printable-planning .emp-name { text-align: left; font-weight: 600; background: #f8f8f8; min-width: 90px; }
        .printable-planning .slot-midi { background: #dbeafe; color: #1e40af; font-size: 10px; padding: 2px 3px; border-radius: 2px; margin-bottom: 2px; }
        .printable-planning .slot-soir { background: #ede9fe; color: #6b21a8; font-size: 10px; padding: 2px 3px; border-radius: 2px; }
        .printable-planning .slot-off { color: #aaa; font-size: 10px; }
        .printable-planning .total-cell { font-weight: 700; background: #f3f4f6; }
        .printable-planning .total-row td { font-weight: 700; background: #e5e7eb; border-top: 2px solid #666; }
        .printable-planning .header-bar { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 3px solid #1a1a2e; }
        .printable-planning .header-bar h1 { font-size: 18px; font-weight: 700; margin: 0; }
        .printable-planning .header-bar .meta { text-align: right; font-size: 11px; color: #666; }
        .printable-planning .footer { margin-top: 12px; display: flex; justify-content: space-between; font-size: 10px; color: #888; }
      `}</style>

      <div className="header-bar">
        <div>
          <h1>Chana Poke Thai</h1>
          <div style={{ fontSize: '13px', fontWeight: 500, marginTop: '2px' }}>
            Planning Semaine {semaine} &mdash; {dateRange}
          </div>
        </div>
        <div className="meta">
          <div>{uniqueEmployes.length} employe(s)</div>
          <div style={{ fontWeight: 700, fontSize: '13px' }}>{grandTotal.toFixed(1)}h total</div>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', width: '100px' }}>Employe</th>
            {weekDates.map((d, i) => (
              <th key={i}>
                <div>{DAYS[i]}</div>
                <div style={{ fontWeight: 400, fontSize: '9px', opacity: 0.8 }}>{format(d, 'dd/MM')}</div>
              </th>
            ))}
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {uniqueEmployes.map(emp => (
            <tr key={emp}>
              <td className="emp-name">{emp}</td>
              {weekDates.map((d, i) => {
                const key = format(d, 'yyyy-MM-dd');
                const cell = grid[emp]?.[key];
                if (!cell || (!cell.midi && !cell.soir)) {
                  return <td key={i} className="slot-off">-</td>;
                }
                return (
                  <td key={i}>
                    {cell.midi && <div className="slot-midi">{cell.midi}</div>}
                    {cell.soir && <div className="slot-soir">{cell.soir}</div>}
                  </td>
                );
              })}
              <td className="total-cell">{employeTotals[emp]?.toFixed(1) || '0.0'}h</td>
            </tr>
          ))}
          <tr className="total-row">
            <td style={{ textAlign: 'left' }}>TOTAL</td>
            {weekDates.map((d, i) => {
              const key = format(d, 'yyyy-MM-dd');
              return <td key={i}>{(dayTotals[key] || 0).toFixed(1)}h</td>;
            })}
            <td>{grandTotal.toFixed(1)}h</td>
          </tr>
        </tbody>
      </table>

      <div className="footer">
        <span>Imprime le {format(new Date(), 'dd/MM/yyyy a HH:mm', { locale: fr })}</span>
        <span>Chana Poke Thai - 11 Place du Marche Neuf</span>
      </div>
    </div>
  );
});
